import { createSlice } from '@reduxjs/toolkit';

import { DayProperties } from '../../../pages/Calendar/Helpers/interfaces';

import { DiscretionaryEventDataProperties, EventDataProperties } from '../../API/ClientAPIHelpers/calendarProperties';
import { UserProperties } from '../../API/userAPIProperties';
import { InfoClientProperties } from '../../API/ClientAPIHelpers/clientProperties';

const today = new Date();
const currentDay = today.getDate();
const currentMonth = today.getMonth();
const currentYear = today.getFullYear();
const weekDayOfFirstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
const lastDayOfPreviousMonth = new Date(currentYear, currentMonth, 0).getDate();

enum CalendarView {
	day,
	month,
	week,
}

interface CalendarHelperStateProperties {
	calendarView: CalendarView;
	day: number;
	displayedCalendarPageDays: DayProperties[];
	filterData: {
		applyFilters: boolean;
		client: InfoClientProperties | null;
		includeBCBAEvents: boolean;
		includeBTEvents: boolean;
		searchClient: string;
		searchTherapist: string;
		showCompleted: boolean;
		showDraftsOnly: boolean;
		therapist: UserProperties | null;
	};
	infoDiscretionaryData: DiscretionaryEventDataProperties | null;
	infoEventData: EventDataProperties | null;
	lastDayOfMonth: number;
	lastDayOfPreviousMonth: number;
	month: number;
	popups: {
		add: boolean;
		addAdmin: boolean;
		addDiscretionary: boolean;
		addInstant: boolean;
		addScheduled: boolean;
		copy: boolean;
		delete: boolean;
		editDiscretionary: boolean;
		filters: boolean;
		info: boolean;
		infoDiscretionary: boolean;
	};
	selectedDay: DayProperties;
	weekDay: number;
	weekView: {
		endOfWeek: number;
		startOfWeek: number;
	};
	year: number;
}

const InitialCalendarHelperState: CalendarHelperStateProperties = {
	day: currentDay,
	month: currentMonth,
	year: currentYear,
	lastDayOfMonth,
	lastDayOfPreviousMonth,
	weekDay: weekDayOfFirstDayOfMonth,
	calendarView: 0,
	displayedCalendarPageDays: [],
	popups: {
		add: false,
		addDiscretionary: false,
		editDiscretionary: false,
		info: false,
		infoDiscretionary: false,
		copy: false,
		delete: false,
		addAdmin: false,
		addInstant: false,
		addScheduled: false,
		filters: false,
	},
	selectedDay: {
		day: currentDay,
		month: currentMonth,
		year: currentYear,
		status: 1,
	},
	infoDiscretionaryData: null,
	infoEventData: null,
	filterData: {
		client: null,
		therapist: null,
		searchClient: '',
		searchTherapist: '',
		applyFilters: false,
		includeBTEvents: false,
		includeBCBAEvents: false,
		showCompleted: false,
		showDraftsOnly: false,
	},
	weekView: {
		startOfWeek: 0,
		endOfWeek: 7,
	},
};

const CalendarHelperSlice = createSlice({
	name: 'calendarHelper',
	initialState: InitialCalendarHelperState,
	reducers: {
		setStartOfWeek(state, action) {
			state.weekView.startOfWeek = action.payload;
		},
		setEndOfWeek(state, action) {
			state.weekView.endOfWeek = action.payload;
		},
		setDays(state, action) {
			state.displayedCalendarPageDays = action.payload;
		},
		setDay(state, action) {
			state.day = action.payload;
		},
		setMonth(state, action) {
			state.month = action.payload;
		},
		setYear(state, action) {
			state.year = action.payload;
		},
		setLastDayOfMonth(state, action) {
			state.lastDayOfMonth = action.payload;
		},
		setLastDayOfPreviousMonth(state, action) {
			state.lastDayOfPreviousMonth = action.payload;
		},
		setWeekDay(state, action) {
			state.weekDay = action.payload;
		},
		setCalendarView(state, action: { payload: CalendarView; type: string }) {
			state.calendarView = action.payload;
		},
		setAdminAddEvent(state, action) {
			state.popups.addAdmin = action.payload;
		},
		setInfoEvent(state, action) {
			state.popups.info = action.payload;
		},
		setCopyEvent(state, action) {
			state.popups.copy = action.payload;
		},
		setDeleteEvent(state, action) {
			state.popups.delete = action.payload;
		},
		setOpenFilters(state, action) {
			state.popups.filters = action.payload;
		},
		setAddSession(state, action) {
			state.popups.add = action.payload;
		},
		setAddInstantSession(state, action) {
			state.popups.addInstant = action.payload;
		},
		setAddScheduledSession(state, action) {
			state.popups.addScheduled = action.payload;
		},
		setAddDiscretionary(state, action) {
			state.popups.addDiscretionary = action.payload;
		},
		setInfoDiscretionary(state, action) {
			state.popups.infoDiscretionary = action.payload;
		},
		setEditDiscretionary(state, action) {
			state.popups.editDiscretionary = action.payload;
		},
		setInfoDiscretionaryData(state, action) {
			state.infoDiscretionaryData = action.payload;
		},
		setInfoEventData(state, action) {
			state.infoEventData = action.payload;
		},
		setSelectedDay(state, action: { payload: DayProperties; type: string }) {
			state.selectedDay = action.payload;
		},
		setTherapist(state, action) {
			state.filterData.therapist = action.payload;

			if (!action.payload) {
				state.filterData.searchTherapist = '';
			}
		},
		setClient(state, action) {
			state.filterData.client = action.payload;

			if (!action.payload) {
				state.filterData.searchClient = '';
			}
		},
		setSearchTherapist(state, action) {
			state.filterData.searchTherapist = action.payload;
		},
		setSearchClient(state, action) {
			state.filterData.searchClient = action.payload;
		},
		setApplyFilter(state, action) {
			state.filterData.applyFilters = action.payload;
		},
		setIncludeBTEvents(state, action) {
			state.filterData.includeBTEvents = action.payload;
		},
		setIncludeBCBAEvents(state, action) {
			state.filterData.includeBCBAEvents = action.payload;
		},
		setShowDraftsOnly(state, action) {
			state.filterData.showDraftsOnly = action.payload;
		},
		setShowCompleted(state, action) {
			state.filterData.showCompleted = action.payload;
		},
		clearFilterData(state) {
			state.filterData = InitialCalendarHelperState.filterData;
		},
		goToNextMonth(state) {
			let newMonth = state.month + 1;
			let newYear = state.year;

			if (newMonth > 11) {
				newMonth = 0;
				newYear += 1;
			}

			state.month = newMonth;
			state.year = newYear;
			state.lastDayOfMonth = new Date(newYear, newMonth + 1, 0).getDate();
			state.lastDayOfPreviousMonth = new Date(newYear, newMonth, 0).getDate();
			state.weekDay = new Date(newYear, newMonth, 1).getDay();
		},
		goToPreviousMonth(state) {
			let newMonth = state.month - 1;
			let newYear = state.year;

			if (newMonth < 0) {
				newMonth = 11;
				newYear -= 1;
			}

			state.month = newMonth;
			state.year = newYear;
			state.lastDayOfMonth = new Date(newYear, newMonth + 1, 0).getDate();
			state.lastDayOfPreviousMonth = new Date(newYear, newMonth, 0).getDate();
			state.weekDay = new Date(newYear, newMonth, 1).getDay();
		},
		goToNextWeek(state) {
			const newStartOfWeek = state.weekView.startOfWeek + 7;
			const newEndOfWeek = state.weekView.endOfWeek + 7;

			if (newStartOfWeek >= state.displayedCalendarPageDays.length) {
				let newMonth = state.month + 1;
				let newYear = state.year;

				if (newMonth > 11) {
					newMonth = 0;
					newYear += 1;
				}

				state.month = newMonth;
				state.year = newYear;
				state.lastDayOfMonth = new Date(newYear, newMonth + 1, 0).getDate();
				state.lastDayOfPreviousMonth = new Date(newYear, newMonth, 0).getDate();
				state.weekDay = new Date(newYear, newMonth, 1).getDay();
				state.weekView.startOfWeek = 0;
				state.weekView.endOfWeek = 7;
			} else {
				state.weekView.startOfWeek = newStartOfWeek;
				state.weekView.endOfWeek = newEndOfWeek;
			}
		},

		goToPreviousWeek(state) {
			const newStartOfWeek = state.weekView.startOfWeek - 7;
			const newEndOfWeek = state.weekView.endOfWeek - 7;

			if (newStartOfWeek < 0) {
				let newMonth = state.month - 1;
				let newYear = state.year;

				if (newMonth < 0) {
					newMonth = 11;
					newYear -= 1;
				}

				state.month = newMonth;
				state.year = newYear;
				state.lastDayOfMonth = new Date(newYear, newMonth + 1, 0).getDate();
				state.lastDayOfPreviousMonth = new Date(newYear, newMonth, 0).getDate();
				state.weekDay = new Date(newYear, newMonth, 1).getDay();

				const totalDaysInCalendar = 42;
				state.weekView.startOfWeek = totalDaysInCalendar - 14;
				state.weekView.endOfWeek = totalDaysInCalendar - 7;
			} else {
				state.weekView.startOfWeek = newStartOfWeek;
				state.weekView.endOfWeek = newEndOfWeek;
			}
		},

		goToNextDay(state) {
			const currentDate = new Date(state.year, state.month, state.day);
			const nextDate = new Date(currentDate);
			nextDate.setDate(currentDate.getDate() + 1);

			state.day = nextDate.getDate();
			state.month = nextDate.getMonth();
			state.year = nextDate.getFullYear();

			if (state.month !== currentDate.getMonth()) {
				state.lastDayOfMonth = new Date(state.year, state.month + 1, 0).getDate();
				state.lastDayOfPreviousMonth = new Date(state.year, state.month, 0).getDate();
				state.weekDay = new Date(state.year, state.month, 1).getDay();
			}
		},

		goToPreviousDay(state) {
			const currentDate = new Date(state.year, state.month, state.day);
			const previousDate = new Date(currentDate);
			previousDate.setDate(currentDate.getDate() - 1);

			state.day = previousDate.getDate();
			state.month = previousDate.getMonth();
			state.year = previousDate.getFullYear();

			if (state.month !== currentDate.getMonth()) {
				state.lastDayOfMonth = new Date(state.year, state.month + 1, 0).getDate();
				state.lastDayOfPreviousMonth = new Date(state.year, state.month, 0).getDate();
				state.weekDay = new Date(state.year, state.month, 1).getDay();
			}
		},
	},
});

export const {
	setLastDayOfMonth,
	setLastDayOfPreviousMonth,
	setDay,
	setMonth,
	setYear,
	setWeekDay,
	setCalendarView,
	setDays,
	setCopyEvent,
	setDeleteEvent,
	setInfoEvent,
	setInfoEventData,
	setSelectedDay,
	setAdminAddEvent,
	setClient,
	setTherapist,
	clearFilterData,
	setAddSession,
	setAddInstantSession,
	setAddScheduledSession,
	setAddDiscretionary,
	setInfoDiscretionary,
	setEditDiscretionary,
	setInfoDiscretionaryData,
	setOpenFilters,
	setEndOfWeek,
	setStartOfWeek,
	setSearchClient,
	setSearchTherapist,
	setApplyFilter,
	setIncludeBCBAEvents,
	setIncludeBTEvents,
	setShowCompleted,
	setShowDraftsOnly,

	goToNextMonth,
	goToPreviousMonth,
	goToNextWeek,
	goToPreviousWeek,
	goToNextDay,
	goToPreviousDay,
} = CalendarHelperSlice.actions;

export default CalendarHelperSlice.reducer;
